import React, { useState, useRef, useEffect, memo } from 'react';
import Navbar from './Navbar';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SplitText from './SplitText';
import Transition from './Transition';
import { DatePicker } from '@heroui/react';

gsap.registerPlugin(ScrollTrigger);

// Memoized component for contact header to prevent re-animation
const ContactHeader = memo(() => (
  <div className="flex flex-col gap-4 text-[#262624]">
    <SplitText
      text="CONTACT"
      className="font-anton uppercase leading-[0.9] text-[16vw] tracking-[-4px] sm:text-[14vw] sm:tracking-[-5px] md:text-[12vw] md:tracking-[-6px] lg:text-[200px] lg:tracking-[-8px]"
      splitType="chars"
      delay={50}
      duration={0.8}
      ease="power3.out"
      from={{ opacity: 0, y: 100 }}
      to={{ opacity: 1, y: 0 }}
      threshold={0}
      rootMargin="+100px"
    />
    <SplitText
      text="Let's start with a quick hello to discuss your project goals, needs, and challenges. I'll answer any questions about my process, and we'll outline the next steps. Looking forward to connecting!"
      className="font-bricolage text-[18px] leading-[1.35] tracking-[-0.4px] sm:text-[20px] sm:leading-[1.3] md:text-[22px] lg:text-[30px] lg:tracking-[-1.2px] lg:leading-[1.05]"
      splitType="lines"
      delay={30}
      duration={0.6}
      ease="power3.out"
      from={{ opacity: 0, y: 20 }}
      to={{ opacity: 1, y: 0 }}
      threshold={0}
      rootMargin="+100px"
    />
  </div>
));

ContactHeader.displayName = 'ContactHeader';

const ContactSection = () => {
  const [date, setDate] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const footerContainerRef = useRef(null);
  const formRef = useRef(null);

  // Functie om datum te converteren naar een leesbaar formaat
  const formatDateForSubmit = (dateValue) => {
    if (!dateValue) return '';
    
    // Als het een CalendarDate object is
    if (dateValue && typeof dateValue === 'object' && 'year' in dateValue) {
      const year = dateValue.year;
      const month = String(dateValue.month).padStart(2, '0');
      const day = String(dateValue.day).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    return dateValue.toString();
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setIsSuccess(false);

    const formData = new FormData(e.target);
    
    try {
      const formObject = Object.fromEntries(formData.entries());

      const response = await fetch('https://formsubmit.co/ajax/info@skdesign.be', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formObject)
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setIsSuccess(true);
        // Reset form
        if (formRef.current) {
          formRef.current.reset();
          setDate(null);
          setSelectedServices([]);
        }
        // Scroll naar success message
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      } else {
        throw new Error(data.message || 'Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Er is een fout opgetreden bij het verzenden. Probeer het opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleService = (service) => {
    setSelectedServices(prev => 
      prev.includes(service) 
        ? prev.filter(s => s !== service)
        : [...prev, service]
    );
  };

  useEffect(() => {
    const footerContainer = footerContainerRef.current;
    if (!footerContainer) return;

    // Set initial width to viewport width
    gsap.set(footerContainer, {
      width: '100vw',
      maxWidth: '100vw'
    });

    // Animate to normal width on scroll
    gsap.to(footerContainer, {
      width: '1400px',
      maxWidth: '1400px',
      ease: 'power2.out',
      scrollTrigger: {
        trigger: footerContainer,
        start: 'top 80%',
        end: 'top 20%',
        scrub: 1.5,
      }
    });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => {
        if (trigger.vars.trigger === footerContainer) {
          trigger.kill();
        }
      });
    };
  }, []);

  return (
    <>
    <Navbar />
    <div className="bg-[#f2f3d9] px-5 py-16">
      <div className="w-full max-w-[1400px] mx-auto relative">
        {/* Contact Content */}
        <div className="px-4 sm:px-6 lg:px-7 py-12 lg:py-16">
          <div className="flex flex-col gap-12 lg:flex-row lg:items-start lg:gap-[50px]">
            {/* Left column */}
            <div className="flex flex-col gap-12 lg:gap-[71px] w-full lg:max-w-[661px]">
              <ContactHeader />

              {/* Datepicker Desktop */}
              <div className="hidden lg:block">
                <div className="relative rounded-[20px] p-[19px] w-full max-w-[651px] h-[651px]">
                  <div className="absolute inset-0 rounded-[20px] border border-dashed border-[#262624]" aria-hidden="true" />
                  <div className="bg-[#262624] rounded-[20px] w-full max-w-[599px] h-[612px] mx-auto flex items-center justify-center">
                    <DatePicker
                      value={date}
                      onChange={setDate}
                      className="w-[82%]"
                      classNames={{
                        base: "w-full",
                        inputWrapper: "h-[72px] rounded-[12px] bg-[#f2f3d9] border border-transparent",
                        input: "text-[#262624] text-[24px] tracking-[-1.2px]",
                        popoverContent: "bg-[#f2f3d9]",
                        calendar: "bg-[#f2f3d9]",
                      }}
                      variant="bordered"
                      aria-label="Preferred date"
                    />
                  </div>
                </div>
              </div>
            </div>
            {/* Right column */}
            <div className="flex flex-col w-full lg:max-w-[690px]">
              {/* Success Message */}
              {isSuccess && (
                <div className="mb-8 p-8 bg-[#f2f3d9] border-2 border-[#262624] rounded-[20px]">
                  <div className="font-bricolage font-extrabold text-[48px] text-[#262624] uppercase tracking-[-2.4px] leading-[1.1] mb-3">
                    ✓ Bericht succesvol verzonden!
                  </div>
                  <div className="font-space-grotesk font-medium text-[20px] text-[#262624] opacity-90">
                    We hebben je bericht ontvangen en nemen zo spoedig mogelijk contact met je op.
                  </div>
                </div>
              )}
              
              <form ref={formRef} onSubmit={handleSubmit} method="POST" action="https://formsubmit.co/info@skdesign.be">
              {/* First & Last name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 w-full">
                <div className="flex flex-col">
                  <div className="font-bricolage font-extrabold uppercase text-[#262624] text-[32px] sm:text-[40px] lg:text-[56px] tracking-[-2px] sm:tracking-[-2.4px] lg:tracking-[-3.36px] leading-[0.9]">First name</div>
                  <div className="mt-5 sm:mt-6">
                    <input
                      name="firstName"
                      required
                      className="w-full bg-transparent text-[#262624] placeholder:text-[#8c8c8c] font-space-grotesk font-bold text-[18px] sm:text-[20px] lg:text-[24px] tracking-[-0.8px] sm:tracking-[-1px] lg:tracking-[-1.2px] pb-2 border-b border-[#262624] outline-none"
                      placeholder="John"
                    />
                  </div>
                </div>
                <div className="flex flex-col">
                  <div className="font-bricolage font-extrabold uppercase text-[#262624] text-[32px] sm:text-[40px] lg:text-[56px] tracking-[-2px] sm:tracking-[-2.4px] lg:tracking-[-3.36px] leading-[0.9]">Last name</div>
                  <div className="mt-5 sm:mt-6">
                    <input
                      name="lastName"
                      required
                      className="w-full bg-transparent text-[#262624] placeholder:text-[#8c8c8c] font-space-grotesk font-bold text-[18px] sm:text-[20px] lg:text-[24px] tracking-[-0.8px] sm:tracking-[-1px] lg:tracking-[-1.2px] pb-2 border-b border-[#262624] outline-none"
                      placeholder="Doe"
                    />
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="mt-10">
                <div className="font-bricolage font-extrabold uppercase text-[#262624] text-[32px] sm:text-[40px] lg:text-[56px] tracking-[-1.8px] sm:tracking-[-2px] lg:tracking-[-2.24px] leading-[0.9]">E-mail</div>
                <div className="mt-5 sm:mt-6">
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full bg-transparent text-[#262624] placeholder:text-[#8c8c8c] font-space-grotesk font-bold text-[18px] sm:text-[20px] lg:text-[24px] tracking-[-0.8px] sm:tracking-[-1px] lg:tracking-[-1.2px] pb-2 border-b border-[#262624] outline-none"
                    placeholder="e-mail address"
                  />
                </div>
              </div>

              {/* Budget */}
              <div className="mt-10">
                <div className="font-bricolage font-extrabold uppercase text-[#262624] text-[32px] sm:text-[40px] lg:text-[56px] tracking-[-1.8px] sm:tracking-[-2px] lg:tracking-[-2.24px] leading-[0.9]">Budget</div>
                <div className="mt-5 sm:mt-6">
                  <input
                    name="budget"
                    className="w-full bg-transparent text-[#262624] placeholder:text-[#8c8c8c] font-space-grotesk font-bold text-[18px] sm:text-[20px] lg:text-[24px] tracking-[-0.8px] sm:tracking-[-1px] lg:tracking-[-1.2px] pb-2 border-b border-[#262624] outline-none"
                    placeholder="$0 - 2000"
                  />
                </div>
              </div>

              {/* Service chips */}
              <div className="mt-12">
                <div className="font-bricolage font-extrabold uppercase text-[#262624] text-[32px] sm:text-[40px] lg:text-[56px] tracking-[-1.8px] sm:tracking-[-2px] lg:tracking-[-2.24px] leading-[0.9]">Service</div>
                <div className="mt-8 sm:mt-10 grid grid-cols-2 gap-4 sm:gap-5 w-full">
                  {['Web design', '3D / VFX', 'Branding', 'Logo design', 'Other'].map((t, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => toggleService(t)}
                      className={`relative rounded-[62px] px-4 py-[12px] border uppercase font-bricolage font-extrabold text-[18px] sm:text-[20px] tracking-[-0.6px] leading-[1] transition-all duration-200 ${
                        selectedServices.includes(t)
                          ? 'bg-[#262624] text-white border-[#262624]'
                          : 'bg-transparent text-[#262624] border-[#262624] hover:bg-[#262624]/10'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div className="mt-12">
                <div className="font-bricolage font-extrabold uppercase text-[#262624] text-[32px] sm:text-[40px] lg:text-[56px] tracking-[-1.8px] sm:tracking-[-2px] lg:tracking-[-2.24px] leading-[0.9]">Message</div>
                <div className="mt-5 sm:mt-6">
                  <input
                    name="message"
                    required
                    className="w-full bg-transparent text-[#262624] placeholder:text-[#8c8c8c] font-space-grotesk font-bold text-[18px] sm:text-[20px] lg:text-[24px] tracking-[-0.8px] sm:tracking-[-1px] lg:tracking-[-1.2px] pb-2 border-b border-[#262624] outline-none"
                    placeholder="Your message"
                  />
                </div>
              </div>

              {/* Datepicker Mobile */}
              <div className="mt-12 lg:hidden">
                <DatePicker
                  value={date}
                  onChange={setDate}
                  className="w-full"
                  classNames={{
                    base: "w-full",
                    label: "font-bricolage font-extrabold uppercase text-[#262624] text-[24px] tracking-[-1px] mb-4 bg-transparent !bg-none",
                    inputWrapper: "h-[56px] rounded-[14px] bg-[#f2f3d9] border border-[#262624]/20",
                    input: "text-[#262624] text-[18px] tracking-[-0.6px]",
                    popoverContent: "bg-[#f2f3d9]",
                    calendar: "bg-[#f2f3d9]",
                  }}
                  label="Preferred date"
                  labelPlacement="outside"
                  variant="bordered"
                />
              </div>

              {/* Hidden metadata */}
              <input type="hidden" name="services" value={selectedServices.join(', ')} />
              <input type="hidden" name="preferredDate" value={formatDateForSubmit(date)} />
              <input type="hidden" name="_captcha" value="false" />
              <input type="hidden" name="_subject" value="New contact form submission" />

              {/* Submit */}
              <div className="mt-10">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`bg-gradient-to-l from-[#f97b22] from-[35.577%] to-[#fe9043] rounded-[62px] flex items-center gap-[12px] px-[22px] py-[12px] contact-btn relative overflow-hidden transition-transform duration-300 hover:scale-[1.03] cursor-pointer h-[66px] w-full lg:w-[266px] ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  <span className="glare" aria-hidden="true"></span>
                  <div className="relative rounded-[62px] flex items-center justify-center flex-1">
                    <div className="font-bricolage font-normal text-[32px] sm:text-[36px] lg:text-[48px] text-[#e4dbd2] uppercase leading-[0.95]" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                      {isSubmitting ? 'Verzenden...' : 'Submit'}
                    </div>
                  </div>
                  <div className="hidden lg:flex w-[48px] h-[48px] relative overflow-hidden rounded-full flex-shrink-0">
                    <svg className="block w-full h-full" fill="none" viewBox="0 0 165 164" preserveAspectRatio="none">
                      <g>
                        <rect width="165" height="164" rx="82" fill="#E4DBD2" />
                        <path className="arrow-path" d="M127.275 77.968L19.5 72.816V91.408L127.275 86.928L69.45 121.2V138L145.5 81.776L69.45 26V43.92L127.275 77.968Z" fill="#262624" />
                      </g>
                    </svg>
                  </div>
                </button>
              </div>
              </form>
            </div>
          </div>
        
        </div>
      </div>

      {/* Footer Section from ContactSection */}
      <div className="bg-[#f2f3d9] px-5 py-0">
        <div ref={footerContainerRef} className="mx-auto border-2 border-[#262624] relative">
          {/* Contact Me SVG */}
          <div className="flex justify-center py-16 relative">
            <Link to="/contact" className="relative" aria-label="Go to contact">
              <svg xmlns="http://www.w3.org/2000/svg" width="1340" height="568" viewBox="0 0 1340 568" fill="none" className="w-full max-w-6xl">
                <mask id="path-1-outside-1_325_605" maskUnits="userSpaceOnUse" x="0.5" y="0" width="1339" height="568" fill="black">
                  <rect fill="white" x="0.5" width="1339" height="568"/>
                  <path d="M67.7274 567C47.8384 567 31.8022 554.471 19.6188 529.414C7.53961 504.357 1.5 469.298 1.5 424.237V167.768C1.5 113.863 6.75862 72.5923 17.2759 43.9554C27.8972 15.3185 45.1309 1 68.977 1C81.9934 1 93.5519 5.84301 103.653 15.529C113.857 25.215 121.876 39.744 127.707 59.1161C133.538 78.2775 136.454 102.493 136.454 131.761V227.779H81.7851V145.658C81.7851 129.024 80.7438 117.864 78.6612 112.179C76.5785 106.283 73.3505 103.335 68.977 103.335C63.8745 103.335 60.4382 107.125 58.668 114.705C56.8978 122.075 56.0126 131.972 56.0126 144.395V422.657C56.0126 438.029 57.106 448.873 59.2928 455.19C61.5837 461.507 64.8117 464.665 68.977 464.665C73.6629 464.665 76.943 460.77 78.8174 452.979C80.7959 445.188 81.7851 435.081 81.7851 422.657V322.533H137.079V427.711C137.079 476.562 130.987 512.042 118.804 534.152C106.62 556.051 89.5949 567 67.7274 567Z"/>
                  <path d="M208.317 567C186.345 567 169.528 553.629 157.865 526.887C146.307 499.935 140.528 461.086 140.528 410.339V146.29C140.528 98.7024 146.255 62.5904 157.709 37.9542C169.268 13.3181 186.137 1 208.317 1C230.497 1 247.314 13.3181 258.768 37.9542C270.327 62.5904 276.106 98.7024 276.106 146.29V410.339C276.106 461.086 270.275 499.935 258.612 526.887C247.054 553.629 230.289 567 208.317 567ZM208.786 464.665C217.116 464.665 221.281 448.346 221.281 415.709V144.395C221.281 117.022 217.22 103.335 209.098 103.335C199.934 103.335 195.353 117.337 195.353 145.343V416.34C195.353 433.607 196.394 446.03 198.477 453.611C200.559 460.98 203.995 464.665 208.786 464.665Z"/>
                  <path d="M285.491 561.946V6.05357H342.034L367.338 272.314V6.05357H420.445V561.946H366.713L339.222 284V561.946H285.491Z"/>
                  <path d="M455.601 561.946V112.81H422.956V6.05357H543.228V112.81H510.583V561.946H455.601Z"/>
                  <path d="M534.181 561.946L560.734 6.05357H653.984L680.068 561.946H628.055L624.15 472.246H591.036L587.6 561.946H534.181ZM594.941 383.492H619.933L607.906 100.808H605.406L594.941 383.492Z"/>
                  <path d="M748.183 567C728.294 567 712.257 554.471 700.074 529.414C687.995 504.357 681.955 469.298 681.955 424.237V167.768C681.955 113.863 687.214 72.5923 697.731 43.9554C708.352 15.3185 725.586 1 749.432 1C762.449 1 774.007 5.84301 784.108 15.529C794.313 25.215 802.331 39.744 808.162 59.1161C813.994 78.2775 816.909 102.493 816.909 131.761V227.779H762.24V145.658C762.24 129.024 761.199 117.864 759.116 112.179C757.034 106.283 753.806 103.335 749.432 103.335C744.33 103.335 740.893 107.125 739.123 114.705C737.353 122.075 736.468 131.972 736.468 144.395V422.657C736.468 438.029 737.561 448.873 739.748 455.19C742.039 461.507 745.267 464.665 749.432 464.665C754.118 464.665 757.398 460.77 759.273 452.979C761.251 445.188 762.24 435.081 762.24 422.657V322.533H817.534V427.711C817.534 476.562 811.442 512.042 799.259 534.152C787.076 556.051 770.05 567 748.183 567Z"/>
                  <path d="M846.755 561.946V112.81H814.11V6.05357H934.382V112.81H901.737V561.946H846.755Z"/>
                  <path d="M999.072 561.946V6.05357H1082.79L1105.91 344.959L1128.87 6.05357H1213.37V561.946H1163.08V161.451L1131.53 561.946H1082.17L1048.74 161.451V561.946H999.072Z"/>
                  <path d="M1224.94 561.946V6.05357H1334.91V113.442H1280.86V221.778H1332.72V326.324H1280.86V453.611H1338.5V561.946H1224.94Z"/>
                </mask>
                <path d="M67.7274 567C47.8384 567 31.8022 554.471 19.6188 529.414C7.53961 504.357 1.5 469.298 1.5 424.237V167.768C1.5 113.863 6.75862 72.5923 17.2759 43.9554C27.8972 15.3185 45.1309 1 68.977 1C81.9934 1 93.5519 5.84301 103.653 15.529C113.857 25.215 121.876 39.744 127.707 59.1161C133.538 78.2775 136.454 102.493 136.454 131.761V227.779H81.7851V145.658C81.7851 129.024 80.7438 117.864 78.6612 112.179C76.5785 106.283 73.3505 103.335 68.977 103.335C63.8745 103.335 60.4382 107.125 58.668 114.705C56.8978 122.075 56.0126 131.972 56.0126 144.395V422.657C56.0126 438.029 57.106 448.873 59.2928 455.19C61.5837 461.507 64.8117 464.665 68.977 464.665C73.6629 464.665 76.943 460.77 78.8174 452.979C80.7959 445.188 81.7851 435.081 81.7851 422.657V322.533H137.079V427.711C137.079 476.562 130.987 512.042 118.804 534.152C106.62 556.051 89.5949 567 67.7274 567Z" fill="#262624"/>
                <path d="M208.317 567C186.345 567 169.528 553.629 157.865 526.887C146.307 499.935 140.528 461.086 140.528 410.339V146.29C140.528 98.7024 146.255 62.5904 157.709 37.9542C169.268 13.3181 186.137 1 208.317 1C230.497 1 247.314 13.3181 258.768 37.9542C270.327 62.5904 276.106 98.7024 276.106 146.29V410.339C276.106 461.086 270.275 499.935 258.612 526.887C247.054 553.629 230.289 567 208.317 567ZM208.786 464.665C217.116 464.665 221.281 448.346 221.281 415.709V144.395C221.281 117.022 217.22 103.335 209.098 103.335C199.934 103.335 195.353 117.337 195.353 145.343V416.34C195.353 433.607 196.394 446.03 198.477 453.611C200.559 460.98 203.995 464.665 208.786 464.665Z" fill="#262624"/>
                <path d="M285.491 561.946V6.05357H342.034L367.338 272.314V6.05357H420.445V561.946H366.713L339.222 284V561.946H285.491Z" fill="#262624"/>
                <path d="M455.601 561.946V112.81H422.956V6.05357H543.228V112.81H510.583V561.946H455.601Z" fill="#262624"/>
                <path d="M534.181 561.946L560.734 6.05357H653.984L680.068 561.946H628.055L624.15 472.246H591.036L587.6 561.946H534.181ZM594.941 383.492H619.933L607.906 100.808H605.406L594.941 383.492Z" fill="#262624"/>
                <path d="M748.183 567C728.294 567 712.257 554.471 700.074 529.414C687.995 504.357 681.955 469.298 681.955 424.237V167.768C681.955 113.863 687.214 72.5923 697.731 43.9554C708.352 15.3185 725.586 1 749.432 1C762.449 1 774.007 5.84301 784.108 15.529C794.313 25.215 802.331 39.744 808.162 59.1161C813.994 78.2775 816.909 102.493 816.909 131.761V227.779H762.24V145.658C762.24 129.024 761.199 117.864 759.116 112.179C757.034 106.283 753.806 103.335 749.432 103.335C744.33 103.335 740.893 107.125 739.123 114.705C737.353 122.075 736.468 131.972 736.468 144.395V422.657C736.468 438.029 737.561 448.873 739.748 455.19C742.039 461.507 745.267 464.665 749.432 464.665C754.118 464.665 757.398 460.77 759.273 452.979C761.251 445.188 762.24 435.081 762.24 422.657V322.533H817.534V427.711C817.534 476.562 811.442 512.042 799.259 534.152C787.076 556.051 770.05 567 748.183 567Z" fill="#262624"/>
                <path d="M846.755 561.946V112.81H814.11V6.05357H934.382V112.81H901.737V561.946H846.755Z" fill="#262624"/>
                <path d="M999.072 561.946V6.05357H1082.79L1105.91 344.959L1128.87 6.05357H1213.37V561.946H1163.08V161.451L1131.53 561.946H1082.17L1048.74 161.451V561.946H999.072Z" fill="#262624"/>
                <path d="M1224.94 561.946V6.05357H1334.91V113.442H1280.86V221.778H1332.72V326.324H1280.86V453.611H1338.5V561.946H1224.94Z" fill="#262624"/>
                <path d="M67.7274 567C47.8384 567 31.8022 554.471 19.6188 529.414C7.53961 504.357 1.5 469.298 1.5 424.237V167.768C1.5 113.863 6.75862 72.5923 17.2759 43.9554C27.8972 15.3185 45.1309 1 68.977 1C81.9934 1 93.5519 5.84301 103.653 15.529C113.857 25.215 121.876 39.744 127.707 59.1161C133.538 78.2775 136.454 102.493 136.454 131.761V227.779H81.7851V145.658C81.7851 129.024 80.7438 117.864 78.6612 112.179C76.5785 106.283 73.3505 103.335 68.977 103.335C63.8745 103.335 60.4382 107.125 58.668 114.705C56.8978 122.075 56.0126 131.972 56.0126 144.395V422.657C56.0126 438.029 57.106 448.873 59.2928 455.19C61.5837 461.507 64.8117 464.665 68.977 464.665C73.6629 464.665 76.943 460.77 78.8174 452.979C80.7959 445.188 81.7851 435.081 81.7851 422.657V322.533H137.079V427.711C137.079 476.562 130.987 512.042 118.804 534.152C106.62 556.051 89.5949 567 67.7274 567Z" stroke="#E4DBD2" strokeWidth="2" mask="url(#path-1-outside-1_325_605)"/>
                <path d="M208.317 567C186.345 567 169.528 553.629 157.865 526.887C146.307 499.935 140.528 461.086 140.528 410.339V146.29C140.528 98.7024 146.255 62.5904 157.709 37.9542C169.268 13.3181 186.137 1 208.317 1C230.497 1 247.314 13.3181 258.768 37.9542C270.327 62.5904 276.106 98.7024 276.106 146.29V410.339C276.106 461.086 270.275 499.935 258.612 526.887C247.054 553.629 230.289 567 208.317 567ZM208.786 464.665C217.116 464.665 221.281 448.346 221.281 415.709V144.395C221.281 117.022 217.22 103.335 209.098 103.335C199.934 103.335 195.353 117.337 195.353 145.343V416.34C195.353 433.607 196.394 446.03 198.477 453.611C200.559 460.98 203.995 464.665 208.786 464.665Z" stroke="#E4DBD2" strokeWidth="2" mask="url(#path-1-outside-1_325_605)"/>
                <path d="M285.491 561.946V6.05357H342.034L367.338 272.314V6.05357H420.445V561.946H366.713L339.222 284V561.946H285.491Z" stroke="#E4DBD2" strokeWidth="2" mask="url(#path-1-outside-1_325_605)"/>
                <path d="M455.601 561.946V112.81H422.956V6.05357H543.228V112.81H510.583V561.946H455.601Z" stroke="#E4DBD2" strokeWidth="2" mask="url(#path-1-outside-1_325_605)"/>
                <path d="M534.181 561.946L560.734 6.05357H653.984L680.068 561.946H628.055L624.15 472.246H591.036L587.6 561.946H534.181ZM594.941 383.492H619.933L607.906 100.808H605.406L594.941 383.492Z" stroke="#E4DBD2" strokeWidth="2" mask="url(#path-1-outside-1_325_605)"/>
                <path d="M748.183 567C728.294 567 712.257 554.471 700.074 529.414C687.995 504.357 681.955 469.298 681.955 424.237V167.768C681.955 113.863 687.214 72.5923 697.731 43.9554C708.352 15.3185 725.586 1 749.432 1C762.449 1 774.007 5.84301 784.108 15.529C794.313 25.215 802.331 39.744 808.162 59.1161C813.994 78.2775 816.909 102.493 816.909 131.761V227.779H762.24V145.658C762.24 129.024 761.199 117.864 759.116 112.179C757.034 106.283 753.806 103.335 749.432 103.335C744.33 103.335 740.893 107.125 739.123 114.705C737.353 122.075 736.468 131.972 736.468 144.395V422.657C736.468 438.029 737.561 448.873 739.748 455.19C742.039 461.507 745.267 464.665 749.432 464.665C754.118 464.665 757.398 460.77 759.273 452.979C761.251 445.188 762.24 435.081 762.24 422.657V322.533H817.534V427.711C817.534 476.562 811.442 512.042 799.259 534.152C787.076 556.051 770.05 567 748.183 567Z" stroke="#E4DBD2" strokeWidth="2" mask="url(#path-1-outside-1_325_605)"/>
                <path d="M846.755 561.946V112.81H814.11V6.05357H934.382V112.81H901.737V561.946H846.755Z" stroke="#E4DBD2" strokeWidth="2" mask="url(#path-1-outside-1_325_605)"/>
                <path d="M999.072 561.946V6.05357H1082.79L1105.91 344.959L1128.87 6.05357H1213.37V561.946H1163.08V161.451L1131.53 561.946H1082.17L1048.74 161.451V561.946H999.072Z" stroke="#E4DBD2" strokeWidth="2" mask="url(#path-1-outside-1_325_605)"/>
                <path d="M1224.94 561.946V6.05357H1334.91V113.442H1280.86V221.778H1332.72V326.324H1280.86V453.611H1338.5V561.946H1224.94Z" stroke="#E4DBD2" strokeWidth="2" mask="url(#path-1-outside-1_325_605)"/>
              </svg>
            </Link>
          </div>

          {/* Navigation Menu */}
          <div className="flex justify-center gap-16 mb-16">
            {[{ label: 'HOME', path: '/' }, { label: 'work', path: '/works' }, { label: 'about me', path: '/about' }, { label: 'contact', path: '/contact' }].map((item, index) => (
              <Link key={index} to={item.path} className="font-bricolage font-extrabold text-[48px] text-[#262624] uppercase leading-[0.907] cursor-pointer hover:text-[#f97b22] transition-colors duration-300">
                {item.label}
              </Link>
            ))}
          </div>

          {/* Bottom Section */}
          <div className="grid grid-cols-2">
            {/* Left Message */}
            <div className="border-2 border-[#262624] p-16 flex items-center justify-center">
              <div className="font-bricolage font-extrabold text-[30px] text-[#262624] uppercase leading-[0.907] text-center">
                Got some exciting ideas? Let's connect and create something extraordinary together!
              </div>
            </div>

            {/* Right Email Container */}
            <div className="border-2 border-[#262624] relative overflow-hidden flex items-center">
              <div className="scrolling-email font-bricolage font-extrabold text-[64px] text-[#262624] uppercase leading-[0.907]">
                sander.kuyken@gmail.com
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-2 border-[#262624] p-16">
            <div className="flex items-center justify-between">
              {/* Copyright */}
              <div className="font-bricolage font-light text-[96px] text-[#262624] uppercase leading-[0.907]">
                ©
              </div>

              {/* Social Links */}
              <div className="flex gap-6">
                <a 
                  href="https://www.artstation.com/kuyken" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="border-2 border-[#262624] rounded-full px-4 py-3 cursor-pointer hover:bg-[#262624] hover:text-[#e4dbd2] transition-all duration-300 group"
                >
                  <div className="font-bricolage font-light text-[32px] text-[#262624] uppercase leading-[0.907] group-hover:text-[#e4dbd2]">
                    artstation
                  </div>
                </a>
                <a 
                  href="https://www.linkedin.com/in/sander-kuyken-8a384716b/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="border-2 border-[#262624] rounded-full px-4 py-3 cursor-pointer hover:bg-[#262624] hover:text-[#e4dbd2] transition-all duration-300 group"
                >
                  <div className="font-bricolage font-light text-[32px] text-[#262624] uppercase leading-[0.907] group-hover:text-[#e4dbd2]">
                    Linkedin
                  </div>
                </a>
              </div>

              {/* Name */}
              <div className="font-bricolage font-light text-[40px] text-[#262624] leading-[0.907]">
                Sander Kuyken
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Transition(ContactSection);
